import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { Input, Select } from '@/shared/components';
import { ROLE_LABELS_DE } from '@/shared/auth';
import type { UserStatusFilter } from '@/shared/api';
import { USER_STATUS_LABELS } from './userStatus';
import {
  EMPTY_USER_FILTERS,
  FILTERABLE_ROLES,
  isUsersFiltered,
  type UsersFilterState,
} from './usersListSearch';

interface Props {
  value: UsersFilterState;
  onChange: (next: UsersFilterState) => void;
}

const STATUS_ORDER: UserStatusFilter[] = ['Active', 'Invited', 'Inactive'];

/**
 * Suche, Rolle und Zustand — genau die drei Filter, die der Server beantwortet.
 *
 * <p>Die Leiste ist rein gesteuert; der Zustand lebt in der URL. Sie sitzt im
 * Einstellungs-Layout und hat dort auch auf 1440 px nur rund 714 px, deshalb umbricht sie
 * früh statt auf einen Viewport-Breakpoint zu setzen.</p>
 */
export function UsersFilterBar({ value, onChange }: Props) {
  // Lokaler Puffer plus Entprellung wie in der Buchungsliste: sonst schriebe jeder Tastendruck
  // die URL. Änderungen von außen (Zurück-Taste, geteilter Link) ziehen den Puffer nach.
  const [draft, setDraft] = useState(value.query);
  useEffect(() => setDraft(value.query), [value.query]);
  useEffect(() => {
    if (draft === value.query) return;
    const id = window.setTimeout(() => onChange({ ...value, query: draft }), 300);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  return (
    <div className="border-line bg-card shadow-card mb-6 flex flex-wrap items-end gap-4 rounded-lg border p-4">
      <div className="flex min-w-56 flex-1 flex-col gap-1.5">
        <label htmlFor="users-search" className="text-ink-soft text-xs font-medium">
          Suche
        </label>
        <div className="relative">
          <Search
            className="text-ink-faint pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
            size={16}
            aria-hidden="true"
          />
          <Input
            id="users-search"
            type="search"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Name oder E-Mail"
            className="min-h-11 pl-9 text-sm"
          />
        </div>
      </div>

      <div className="flex min-w-44 flex-1 flex-col gap-1.5">
        <label htmlFor="users-role" className="text-ink-soft text-xs font-medium">
          Rolle
        </label>
        <Select
          id="users-role"
          value={value.role}
          onChange={(e) => onChange({ ...value, role: e.target.value as UsersFilterState['role'] })}
          className="min-h-11 text-sm"
        >
          <option value="">Alle Rollen</option>
          {FILTERABLE_ROLES.map((role) => (
            <option key={role} value={role}>
              {ROLE_LABELS_DE[role]}
            </option>
          ))}
        </Select>
      </div>

      <div className="flex min-w-44 flex-1 flex-col gap-1.5">
        <label htmlFor="users-status" className="text-ink-soft text-xs font-medium">
          Zustand
        </label>
        <Select
          id="users-status"
          value={value.status}
          onChange={(e) =>
            onChange({ ...value, status: e.target.value as UsersFilterState['status'] })
          }
          className="min-h-11 text-sm"
        >
          <option value="">Alle Zustände</option>
          {STATUS_ORDER.map((status) => (
            <option key={status} value={status}>
              {USER_STATUS_LABELS[status]}
            </option>
          ))}
        </Select>
      </div>

      {isUsersFiltered(value) && (
        <button
          type="button"
          onClick={() => onChange(EMPTY_USER_FILTERS)}
          className="text-ink-soft hover:text-sealwax min-h-11 self-end text-xs underline-offset-4 transition-colors hover:underline"
        >
          Filter zurücksetzen
        </button>
      )}
    </div>
  );
}
